/**
 * AI Templates — instruction templates for the clinical-summary generation
 * service. Templates are keyed by a stable slug; each edit PUBLISHES a new
 * immutable version, and the template's active version pointer is what the
 * summary service reads directly from Postgres.
 *
 * GraphQL operations (backend resolvers live in care-management-gql, NOT this
 * repo — the ops error until the backend adds them):
 *   Query:    AcAiTemplates            → aiTemplates[]  (list + activeVersion.version)
 *   Query:    AcAiTemplate($key)       → aiTemplate     (full, incl activeVersion body)
 *   Query:    AcAiTemplateVersions     → aiTemplateVersions[] (version history)
 *   Mutation: AcCreateAiTemplate, AcPublishAiTemplateVersion,
 *             AcSetActiveAiTemplateVersion, AcSetAiTemplateActive
 *
 * Schema-miss behavior mirrors PersonasPage: an amber note + "No templates
 * found" rather than a hard failure.
 *
 * DISABLED TEMPLATES ARE HIDDEN BY DEFAULT behind a "Show disabled (N)" filter.
 * Disabling is the only way to retire a template — the rows are undeletable
 * (visit_note_generation.ai_template_version_id references ai_template_version
 * with no ON DELETE), so retired legacy keys accumulate and would otherwise sit
 * alongside the live ones forever.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { ChevronDown, ChevronRight, History, Plus, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminWall, isAuthWallError } from "../components/AdminWall";
import MarkdownEditor from "../components/MarkdownEditor";
import {
  LIST_AI_TEMPLATES_QUERY,
  AI_TEMPLATE_QUERY,
  AI_TEMPLATE_VERSIONS_QUERY,
  CREATE_AI_TEMPLATE_MUTATION,
  PUBLISH_AI_TEMPLATE_VERSION_MUTATION,
  SET_ACTIVE_AI_TEMPLATE_VERSION_MUTATION,
  SET_AI_TEMPLATE_ACTIVE_MUTATION,
  SET_AI_TEMPLATE_PREVISIT_DATA_MUTATION,
  type ListAiTemplatesResult,
  type AiTemplateListItem,
  type AiTemplateResult,
  type AiTemplateVars,
  type AiTemplateVersionsResult,
  type AiTemplateVersionsVars,
  type CreateAiTemplateResult,
  type CreateAiTemplateVars,
  type PublishAiTemplateVersionResult,
  type PublishAiTemplateVersionVars,
  type SetActiveAiTemplateVersionResult,
  type SetActiveAiTemplateVersionVars,
  type SetAiTemplateActiveResult,
  type SetAiTemplateActiveVars,
  type SetAiTemplatePrevisitDataResult,
  type SetAiTemplatePrevisitDataVars,
} from "../graphql/adminOps";

function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---- Create Template Modal ----
function CreateTemplateModal({
  existingKeys,
  onClose,
  onCreated,
}: {
  existingKeys: string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");

  const [createTemplate, { loading }] = useMutation<
    CreateAiTemplateResult,
    CreateAiTemplateVars
  >(CREATE_AI_TEMPLATE_MUTATION, {
    onCompleted: () => {
      toast.success("Template created");
      onCreated();
      onClose();
    },
    onError: (e) => toast.error(`Create failed: ${e.message}`),
  });

  const trimmedKey = key.trim();
  const trimmedName = name.trim();
  const trimmedInstructions = instructions.trim();
  const dupKey = trimmedKey.length > 0 && existingKeys.some((k) => k === trimmedKey);
  const canSubmit =
    trimmedKey.length > 0 &&
    trimmedName.length > 0 &&
    trimmedInstructions.length > 0 &&
    !dupKey &&
    !loading;

  const submit = () => {
    if (!canSubmit) return;
    void createTemplate({
      variables: {
        key: trimmedKey,
        name: trimmedName,
        description: description.trim() || undefined,
        instructions: trimmedInstructions,
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Create AI template"
    >
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">New AI Template</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="tmpl-key" className="block text-sm font-medium mb-1">
                Key <span className="text-destructive">*</span>
              </label>
              <input
                id="tmpl-key"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="e.g. discharge_summary"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
              />
              {dupKey && (
                <p className="text-xs text-destructive mt-1">A template with this key already exists.</p>
              )}
            </div>
            <div>
              <label htmlFor="tmpl-name" className="block text-sm font-medium mb-1">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                id="tmpl-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Discharge Summary"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <div>
            <label htmlFor="tmpl-desc" className="block text-sm font-medium mb-1">Description</label>
            <input
              id="tmpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description (optional)"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div>
            <label htmlFor="tmpl-instructions" className="block text-sm font-medium mb-1">
              Instructions <span className="text-destructive">*</span>
            </label>
            <MarkdownEditor
              id="tmpl-instructions"
              value={instructions}
              onChange={setInstructions}
              placeholder="The AI instruction body for version 1…"
              rows={8}
              ariaLabel="Instructions"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors"
            >
              {loading ? "Creating..." : "Create Template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Toggle (mirrors FeatureFlagsPage) ----
function Toggle({
  enabled,
  onChange,
  disabled,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={
        "relative inline-flex w-10 h-6 rounded-full border-2 transition-colors duration-200 " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
        (enabled ? "bg-primary border-primary" : "bg-muted border-border") +
        " disabled:opacity-40 disabled:cursor-not-allowed"
      }
    >
      <span
        className={
          "absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 " +
          (enabled ? "translate-x-4" : "translate-x-0")
        }
      />
    </button>
  );
}

// ---- Version history list ----
function VersionHistory({
  templateId,
  activeVersionId,
  onSetActive,
  settingActive,
}: {
  templateId: string;
  activeVersionId: string | null;
  onSetActive: (versionId: string) => void;
  settingActive: boolean;
}) {
  const { data, loading, error } = useQuery<AiTemplateVersionsResult, AiTemplateVersionsVars>(
    AI_TEMPLATE_VERSIONS_QUERY,
    { variables: { templateId }, fetchPolicy: "cache-and-network" },
  );

  const versions = data?.aiTemplateVersions ?? [];

  if (loading && versions.length === 0) {
    return <LoadingSpinner fullScreen={false} label="Loading version history..." />;
  }
  if (error && versions.length === 0) {
    return (
      <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
        Could not load version history: {error.message}
      </p>
    );
  }
  if (versions.length === 0) {
    return <p className="text-sm text-muted-foreground italic">No versions yet.</p>;
  }

  return (
    <div className="space-y-2">
      {versions
        .slice()
        .sort((a, b) => b.version - a.version)
        .map((v) => {
          const isActive = v.id === activeVersionId;
          const created = fmtDate(v.createdAt);
          return (
            <div
              key={v.id}
              className={
                "rounded-lg border bg-card px-3 py-2 " +
                (isActive ? "border-primary/60" : "border-border")
              }
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold">v{v.version}</span>
                {isActive && (
                  <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold bg-primary/10 text-primary border-primary/40">
                    ACTIVE
                  </span>
                )}
                {v.model && (
                  <span className="text-[10px] font-mono text-muted-foreground/70">{v.model}</span>
                )}
                <span className="ml-auto text-[10px] text-muted-foreground/60">
                  {created}
                  {v.createdBy ? ` by ${v.createdBy}` : ""}
                </span>
                {!isActive && (
                  <button
                    type="button"
                    disabled={settingActive}
                    onClick={() => onSetActive(v.id)}
                    className="text-xs font-medium px-2 py-0.5 rounded border border-border hover:bg-accent disabled:opacity-40 transition-colors"
                  >
                    Set active
                  </button>
                )}
              </div>
              {v.notes && (
                <p className="text-xs text-muted-foreground mt-1 m-0">{v.notes}</p>
              )}
              <pre className="text-xs font-mono text-muted-foreground mt-1 whitespace-pre-wrap break-words max-h-24 overflow-y-auto bg-muted/30 rounded p-2 m-0">
                {v.instructions}
              </pre>
            </div>
          );
        })}
    </div>
  );
}

// ---- Template Row (expandable editor) ----
function TemplateRow({
  tmpl,
  onRefresh,
  onActiveChanged,
}: {
  tmpl: AiTemplateListItem;
  onRefresh: () => void;
  /** Lets the list keep a just-disabled row on screen instead of vanishing it. */
  onActiveChanged?: (isActive: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [notes, setNotes] = useState("");
  const [model, setModel] = useState("");

  // Lazy-load the full template (incl active version instructions) only when
  // the row is expanded — the list query only carries activeVersion.version.
  const [loadTemplate, { data: fullData, loading: loadingFull, refetch: refetchFull }] =
    useLazyQuery<AiTemplateResult, AiTemplateVars>(AI_TEMPLATE_QUERY, {
      variables: { key: tmpl.key },
      fetchPolicy: "cache-and-network",
    });

  const full = fullData?.aiTemplate ?? null;
  const activeVersion = full?.activeVersion ?? null;

  useEffect(() => {
    if (expanded) void loadTemplate();
  }, [expanded, loadTemplate]);

  // Prefill the editor from the active version once it loads.
  useEffect(() => {
    if (activeVersion) setInstructions(activeVersion.instructions);
  }, [activeVersion]);

  const refetchAll = () => {
    onRefresh();
    void refetchFull?.();
  };

  const [publishVersion, { loading: publishing }] = useMutation<
    PublishAiTemplateVersionResult,
    PublishAiTemplateVersionVars
  >(PUBLISH_AI_TEMPLATE_VERSION_MUTATION, {
    onCompleted: () => {
      toast.success("New version published and set active");
      setNotes("");
      setModel("");
      refetchAll();
    },
    onError: (e) => toast.error(`Publish failed: ${e.message}`),
    // Version history depends on this template's versions.
    refetchQueries: [{ query: AI_TEMPLATE_VERSIONS_QUERY, variables: { templateId: tmpl.id } }],
  });

  const [setActiveVersion, { loading: settingActive }] = useMutation<
    SetActiveAiTemplateVersionResult,
    SetActiveAiTemplateVersionVars
  >(SET_ACTIVE_AI_TEMPLATE_VERSION_MUTATION, {
    onCompleted: () => {
      toast.success("Active version updated");
      refetchAll();
    },
    onError: (e) => toast.error(`Set active failed: ${e.message}`),
  });

  const [setTemplateActive, { loading: togglingActive }] = useMutation<
    SetAiTemplateActiveResult,
    SetAiTemplateActiveVars
  >(SET_AI_TEMPLATE_ACTIVE_MUTATION, {
    onCompleted: (d) => {
      toast.success(d.setAiTemplateActive.isActive ? "Template enabled" : "Template disabled");
      onActiveChanged?.(d.setAiTemplateActive.isActive);
      onRefresh();
    },
    onError: (e) => toast.error(`Toggle failed: ${e.message}`),
  });

  const [setPrevisitData, { loading: togglingPrevisit }] = useMutation<
    SetAiTemplatePrevisitDataResult,
    SetAiTemplatePrevisitDataVars
  >(SET_AI_TEMPLATE_PREVISIT_DATA_MUTATION, {
    onCompleted: (d) => {
      toast.success(
        d.setAiTemplatePrevisitData.usesPrevisitData
          ? "Now sources open care gaps + last-visit history"
          : "Now sources full history, no care gaps",
      );
      onRefresh();
    },
    onError: (e) => toast.error(`Data pipeline change failed: ${e.message}`),
  });

  const publish = () => {
    const body = instructions.trim();
    if (!body) {
      toast.error("Instructions cannot be empty");
      return;
    }
    void publishVersion({
      variables: {
        templateId: tmpl.id,
        instructions: body,
        model: model.trim() || undefined,
        notes: notes.trim() || undefined,
        setActive: true,
      },
    });
  };

  const dirty = !!activeVersion && instructions.trim() !== activeVersion.instructions.trim();
  const activeVersionNo = tmpl.activeVersion?.version ?? null;

  return (
    <div className="border border-border rounded-xl overflow-hidden mb-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-card hover:bg-muted/30 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
        <span className="font-semibold">{tmpl.name}</span>
        <span className="text-xs text-muted-foreground font-mono">{tmpl.key}</span>
        {activeVersionNo != null && (
          <span className="text-xs text-muted-foreground font-mono">v{activeVersionNo}</span>
        )}
        <span
          className={
            "ml-auto inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold " +
            (tmpl.isActive
              ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700"
              : "bg-muted text-muted-foreground border-border")
          }
        >
          {tmpl.isActive ? "Active" : "Disabled"}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-border bg-muted/10 px-4 py-3">
          {tmpl.description && (
            <p className="text-xs text-muted-foreground mb-3 m-0">{tmpl.description}</p>
          )}

          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 text-sm">
              <Toggle
                enabled={tmpl.isActive}
                onChange={(v) => void setTemplateActive({ variables: { templateId: tmpl.id, isActive: v } })}
                disabled={togglingActive}
              />
              <span className="text-muted-foreground">
                {tmpl.isActive ? "Enabled (read by the summary service)" : "Disabled (soft-off)"}
              </span>
            </label>
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-accent transition-colors"
            >
              <History className="w-3.5 h-3.5" />
              {showHistory ? "Hide history" : "Version history"}
            </button>
          </div>

          {/*
            DATA pipeline — separate from the prompt body above it on purpose.
            This chooses what the summary service FEEDS the prompt, not what the
            prompt says. The pre-round pipeline injects a verbatim care-gap list
            and labs table; a template whose instructions have no slot for those
            will render them as stray blocks, which is exactly the QA defect this
            control exists to make visible and fixable.

            Changing it publishes NO new version, and cached summaries invalidate
            on their own (the resolved strategy is part of the cache key).
          */}
          <label className="flex items-start gap-2 text-sm mb-3">
            <Toggle
              enabled={tmpl.usesPrevisitData}
              onChange={(v) =>
                void setPrevisitData({
                  variables: { templateId: tmpl.id, usesPrevisitData: v },
                })
              }
              disabled={togglingPrevisit}
            />
            <span className="text-muted-foreground">
              {tmpl.usesPrevisitData
                ? "Pre-round data: open care gaps + history since the last visit"
                : "Full-history data: no care gaps, no last-visit scoping"}
              <span className="block text-xs opacity-80">
                Only turn this on for a pre-round briefing template — the gaps and
                labs blocks are inserted verbatim and need a slot in the prompt.
              </span>
            </span>
          </label>

          {loadingFull && !full ? (
            <LoadingSpinner fullScreen={false} label="Loading template..." />
          ) : (
            <>
              <label htmlFor={`instr-${tmpl.id}`} className="block text-sm font-medium mb-1">
                Instructions
                {activeVersion && (
                  <span className="text-xs text-muted-foreground font-normal">
                    {" "}
                    (active v{activeVersion.version})
                  </span>
                )}
              </label>
              <MarkdownEditor
                id={`instr-${tmpl.id}`}
                value={instructions}
                onChange={setInstructions}
                rows={10}
                ariaLabel="Instructions"
              />

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label htmlFor={`model-${tmpl.id}`} className="block text-sm font-medium mb-1">
                    Model <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <input
                    id={`model-${tmpl.id}`}
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="e.g. gpt-4o"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label htmlFor={`notes-${tmpl.id}`} className="block text-sm font-medium mb-1">
                    Notes <span className="text-xs text-muted-foreground font-normal">(changelog)</span>
                  </label>
                  <input
                    id={`notes-${tmpl.id}`}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Why this change?"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={publish}
                  disabled={publishing || !instructions.trim()}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors"
                >
                  {publishing ? "Publishing..." : "Publish new version"}
                </button>
                {dirty && (
                  <span className="text-xs text-amber-700 dark:text-amber-400">Unpublished changes</span>
                )}
              </div>
            </>
          )}

          {showHistory && (
            <div className="mt-4 pt-3 border-t border-border">
              <p className="text-sm font-medium text-muted-foreground mb-2">Version history</p>
              <VersionHistory
                templateId={tmpl.id}
                activeVersionId={activeVersion?.id ?? null}
                settingActive={settingActive}
                onSetActive={(versionId) =>
                  void setActiveVersion({ variables: { templateId: tmpl.id, versionId } })
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Main Page ----
export default function AiTemplatesPage() {
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useQuery<ListAiTemplatesResult>(LIST_AI_TEMPLATES_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  const [creating, setCreating] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);

  const effective = data ?? previousData;
  const templates = effective?.aiTemplates ?? [];
  const listFailed = !!error && !effective?.aiTemplates;
  const existingKeys = templates.map((t) => t.key);

  // Disabling is how a template is RETIRED — the rows cannot be deleted
  // (visit_note_generation.ai_template_version_id references ai_template_version
  // with no ON DELETE, so a cascade fails for any template that ever generated a
  // note). So the list hides disabled templates by default and keeps them one
  // click away, rather than leaving retired rows interleaved with live ones.
  //
  // NOTE: `isActive` here is the TEMPLATE's enabled flag. It is a different
  // concept from a VERSION's active flag (which version the summary service
  // reads) — see VersionHistory. Do not conflate them.
  const disabledCount = templates.filter((t) => !t.isActive).length;
  const visibleTemplates = showDisabled
    ? templates
    : templates.filter((t) => t.isActive);

  return (
    <div>
      <AdminWall error={error} screenLabel="AI Templates">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold">AI Templates</h1>
              <button
                type="button"
                onClick={() => void refetch()}
                className="p-2 rounded-lg border border-border hover:bg-accent transition-colors"
                aria-label="Refresh"
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              Instruction templates for the clinical-summary service. Editing publishes a new
              immutable version; the active version is what the summary service reads.
            </p>
            {listFailed && !isAuthWallError(error) && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                <strong>Note:</strong> aiTemplates query not yet on schema. The backend
                (care-management-gql) needs to expose the AI template resolvers.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors shrink-0"
          >
            <Plus className="w-4 h-4" />
            New Template
          </button>
        </div>

        {disabledCount > 0 && (
          <label className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
            />
            Show disabled ({disabledCount})
          </label>
        )}

        {loading && templates.length === 0 ? (
          <LoadingSpinner fullScreen={false} label="Loading AI templates..." />
        ) : templates.length === 0 ? (
          <p className="text-muted-foreground text-sm">No templates found.</p>
        ) : visibleTemplates.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Every template is disabled. Tick “Show disabled ({disabledCount})” to
            see them.
          </p>
        ) : (
          <div>
            {visibleTemplates.map((t) => (
              <TemplateRow
                key={t.id}
                tmpl={t}
                onRefresh={() => void refetch()}
                onActiveChanged={(isActive) => {
                  // Disabling from inside an expanded row would otherwise make
                  // that row disappear mid-interaction.
                  if (!isActive) setShowDisabled(true);
                }}
              />
            ))}
          </div>
        )}
      </AdminWall>

      {creating && (
        <CreateTemplateModal
          existingKeys={existingKeys}
          onClose={() => setCreating(false)}
          onCreated={() => void refetch()}
        />
      )}
    </div>
  );
}
